<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue';
    import { useRouter } from 'vue-router';
    import { getCodeById } from '@/shared/helpers/api';
    import { Code } from '@/shared/helpers/api/types/entities.ts';
    import { requireCanEditCode as vRequireCanEditCode } from '@/shared/directives/requirePermission';
    import { decodeIdBase64 } from '@/shared/helpers/utils';
    import { http } from '@/shared/httpWrapper';
    import ViewBaseDetails from '@/apps/facilityAndProject/ViewBaseDetails.vue';

    const props = defineProps<{
        id: string | null;
    }>();

    const router = useRouter();

    const contract = ref<Code | null>(null);

    const attributes = computed(() =>
        contract.value
            ? contract.value.attributes.map(a => ({ name: a.definitionName, value: a.displayValue }))
            : []
    );

    async function loadContract() {
        contract.value = await getCodeById(http, Number(decodeIdBase64(props.id!)));
    }

    function editContract() {
        router.push({ name: 'EditContract' });
    }

    async function onRefresh() {
        router.push({ name: 'ContractById' });
        await loadContract();
    }

    function onCancel() {
        router.push({ name: 'ContractById' });
    }

    onMounted(async () => {
        await loadContract();
    });
</script>

<template>
    <view-base-details>
        <template
            v-if="contract"
            #title>
            <h1 class="title">
                Contract: {{ contract.name }} - {{ contract.description }}
            </h1>
        </template>
        <template #controls>
            <router-link :to="{ name: 'ContractList' }">
                &laquo; Back to list
            </router-link>
        </template>
        <template #edit>
            <router-view
                :code-id="id"
                title="Edit contract"
                library="Contract"
                @onRefresh="onRefresh"
                @onCancel="onCancel" />
        </template>
        <template
            v-if="contract"
            #details>
            <div class="columns">
                <div class="column is-6">
                    <div class="panel is-success">
                        <div class="panel-heading is-flex is-justify-content-space-between">
                            <p>
                                Contract attributes
                            </p>
                            <b-button
                                v-require-can-edit-code="{ libraryName: 'Contract' }"
                                class="is-primary"
                                @click="editContract">
                                <b-icon
                                    icon="pencil"
                                    size="is-small" />
                                <span>
                                    Edit
                                </span>
                            </b-button>
                        </div>
                        <div class="panel-block">
                            <b-table
                                class="headless-table"
                                :data="attributes"
                                :striped="false"
                                :narrowed="true"
                                :hoverable="false">
                                <b-table-column
                                    v-slot="col"
                                    field="name"
                                    label="Name">
                                    <b>{{ col.row.name }}</b>
                                </b-table-column>
                                <b-table-column
                                    v-slot="col"
                                    field="value"
                                    label="Value">
                                    {{ col.row.value }}
                                </b-table-column>
                            </b-table>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </view-base-details>
</template>
